// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {Button} from '@app/features/ui/button/Button';
import {openBadgeSettingsModal} from '@app/features/user/commands/BadgeSettingsModalCommands';
import type {User} from '@app/features/user/models/User';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useContext} from 'react';
import {BadgeInventoryContext} from './BadgeInventoryState';
import styles from './EditBadgesButton.module.css';

const TITLE = msg({message: 'Badges', comment: 'Title of the owned badge inventory in profile settings.'});
const DESCRIPTION = msg({
	message: 'Choose which of your badges appear on your profile and in what order.',
	comment: 'Description of the owned badge visibility and order settings.',
});
const EDIT = msg({
	message: 'Edit badges',
	comment: 'Button in the profile settings opening the popup where badges are ordered and hidden.',
});

export const EditBadgesButton = observer(({user}: {user: User}) => {
	const {i18n} = useLingui();
	const inventory = useContext(BadgeInventoryContext);
	if (RuntimeConfig.features.badge_inventory_enabled !== true) return null;
	return (
		<Button
			small
			variant="secondary"
			fitContent
			onClick={() => openBadgeSettingsModal(user, inventory)}
			data-flx="user.my-profile-tab.edit-badges-button.open"
		>
			{i18n._(EDIT)}
		</Button>
	);
});

export const BadgeSettingsEntry = ({user, children}: {user: User; children?: ReactNode}) => {
	const {i18n} = useLingui();
	return (
		<section
			className={styles.section}
			aria-label={i18n._(TITLE)}
			data-flx="user.my-profile-tab.badge-settings-entry.section"
		>
			<h2 className={styles.title} data-flx="user.my-profile-tab.badge-settings-entry.title">
				{i18n._(TITLE)}
			</h2>
			<p className={styles.description} data-flx="user.my-profile-tab.badge-settings-entry.description">
				{i18n._(DESCRIPTION)}
			</p>
			<div data-flx="user.my-profile-tab.badge-settings-entry.action">
				<EditBadgesButton user={user} data-flx="user.my-profile-tab.badge-settings-entry.edit-badges-button" />
			</div>
			{children}
		</section>
	);
};
